import React, { useState, useEffect, useCallback } from "react";
import {
  TOTAL_SCREENS,
  GET_SCREEN_INDEX,
} from "../../../utilities/commonUtils";
import ScrollService from "../../../utilities/ScrollService";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./Header.css";
import { useTranslation } from "react-i18next";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [selectedScreen, setSelectedScreen] = useState(0);
  const [showHeaderOptions, setShowHeaderOptions] = useState(false);

  const closeMenu = useCallback(() => {
    setShowHeaderOptions(false);
  }, []);

  const updateCurrentScreen = (currentScreen) => {
    if (!currentScreen || !currentScreen.screenInView) return;
    const screenIndex = GET_SCREEN_INDEX(currentScreen.screenInView);
    if (screenIndex < 0) return;
    setSelectedScreen(screenIndex);
  };

  const currentScreenSubscription =
    ScrollService.currentScreenBroadcaster.subscribe(updateCurrentScreen);

  const getHeaderOptionsClasses = (index) => {
    let classes = "header-option ";
    if (selectedScreen === index) classes += "selected-header-option ";
    return classes;
  };

  const switchScreen = (index, screen) => {
    const screenComponent = document.getElementById(screen.screen_name);
    if (!screenComponent) return;
    screenComponent.scrollIntoView({ behavior: "smooth" });
    setSelectedScreen(index);
    closeMenu();
  };

  const renderNavOptions = () =>
    TOTAL_SCREENS.map((Screen, i) => (
      <div
        key={Screen.screen_name}
        className={getHeaderOptionsClasses(i)}
        onClick={() => switchScreen(i, Screen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            switchScreen(i, Screen);
          }
        }}
      >
        <span>{t(`header.${Screen.screen_name}`)}</span>
      </div>
    ));

  const renderLanguageSwitch = (className = "") => (
    <div className={`language-switch-header ${className}`.trim()}>
      <button
        type="button"
        className={i18n.language === "en" ? "active" : ""}
        onClick={() => i18n.changeLanguage("en")}
      >
        EN
      </button>
      <button
        type="button"
        className={i18n.language === "tr" ? "active" : ""}
        onClick={() => i18n.changeLanguage("tr")}
      >
        TR
      </button>
    </div>
  );

  useEffect(() => {
    return () => {
      currentScreenSubscription.unsubscribe();
    };
  }, [currentScreenSubscription]);

  useEffect(() => {
    document.body.style.overflow = showHeaderOptions ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showHeaderOptions]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeMenu]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 970) closeMenu();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [closeMenu]);

  return (
    <header className="header-container">
      <div className="header-parent">
        <div
          className="header-logo"
          onClick={() => switchScreen(0, TOTAL_SCREENS[0])}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              switchScreen(0, TOTAL_SCREENS[0]);
            }
          }}
        >
          <span>Ensar Hafızoğlu</span>
        </div>

        <nav className="header-options header-options--desktop" aria-label="Main navigation">
          {renderNavOptions()}
        </nav>

        <div className="header-actions">
          {renderLanguageSwitch("language-switch-header--desktop")}
          <button
            type="button"
            className="header-hamburger"
            aria-label={showHeaderOptions ? "Close menu" : "Open menu"}
            aria-expanded={showHeaderOptions}
            onClick={() => setShowHeaderOptions((prev) => !prev)}
          >
            <FontAwesomeIcon
              className="header-hamburger-bars"
              icon={showHeaderOptions ? faTimes : faBars}
            />
          </button>
        </div>
      </div>

      <div
        className={`header-overlay ${showHeaderOptions ? "is-visible" : ""}`}
        onClick={closeMenu}
        aria-hidden={!showHeaderOptions}
      />

      <nav
        className={`header-options header-options--mobile ${
          showHeaderOptions ? "is-open" : ""
        }`}
        aria-label="Mobile navigation"
        aria-hidden={!showHeaderOptions}
      >
        <div className="header-mobile-menu-inner">
          {renderNavOptions()}
          {renderLanguageSwitch("language-switch-header--mobile")}
        </div>
      </nav>
    </header>
  );
}
